import { useState } from "react";
import { Check, CheckCheck, Clock } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface MessageStatusProps {
  createdAt: string;
  deliveredAt: string | null;
  readAt: string | null;
  isRead: boolean;
  isFromAgent: boolean;
  className?: string;
}

export function MessageStatus({
  createdAt,
  deliveredAt,
  readAt,
  isRead,
  isFromAgent,
  className = "",
}: MessageStatusProps) {
  const [showDetails, setShowDetails] = useState(false);

  // Only show status for user's own messages (not agent messages)
  if (isFromAgent) {
    return null;
  }

  const formatTime = (dateStr: string | null) => {
    if (!dateStr) return null;
    return format(new Date(dateStr), "HH:mm", { locale: fr });
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return null;
    return format(new Date(dateStr), "d MMM à HH:mm", { locale: fr });
  };

  // Status determination:
  // - Just sent (no delivered_at): single gray check
  // - Delivered (has delivered_at but not read): double gray checks
  // - Read (is_read = true): double blue checks
  const getStatusIcon = () => {
    if (isRead && readAt) {
      // Read - double blue checks
      return (
        <CheckCheck 
          size={14} 
          className="text-sky-400" 
        />
      );
    } else if (deliveredAt) {
      // Delivered - double gray checks
      return (
        <CheckCheck 
          size={14} 
          className="text-primary-foreground/60" 
        />
      );
    } else {
      // Sent - single check
      return (
        <Check 
          size={14} 
          className="text-primary-foreground/50" 
        />
      );
    }
  };

  const getStatusLabel = () => {
    if (isRead && readAt) {
      return "Lu";
    } else if (deliveredAt) {
      return "Reçu";
    } else {
      return "Envoyé";
    }
  };

  const tooltipContent = (
    <div className="space-y-1.5 text-xs">
      <div className="flex items-center gap-2">
        <Check size={12} className="text-muted-foreground" />
        <span>Envoyé: {formatDateTime(createdAt)}</span>
      </div>
      {deliveredAt && (
        <div className="flex items-center gap-2">
          <CheckCheck size={12} className="text-muted-foreground" />
          <span>Reçu: {formatDateTime(deliveredAt)}</span>
        </div>
      )}
      {isRead && readAt && (
        <div className="flex items-center gap-2">
          <CheckCheck size={12} className="text-sky-400" />
          <span>Lu: {formatDateTime(readAt)}</span>
        </div>
      )}
    </div>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={`cursor-pointer ${className}`}>
          {getStatusIcon()}
        </span>
      </TooltipTrigger>
      <TooltipContent side="left" className="bg-popover border border-border">
        {tooltipContent}
      </TooltipContent>
    </Tooltip>
  );
}
